import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { hydrate } from "@/lib";
import type {
  Brainstormer,
  Idea,
  IdeaComments,
  ResponsePayload
} from "@/types";
import {
  fetchIdeas,
  fetchBrainstormer,
  saveIdea,
  updateOneIdea,
  refreshOneIdea,
  saveVote,
  removeVote,
  fetchIdea,
  fetchComments,
  reportIdea,
  reportIdeaComment,
  fetchBrainstormers,
  fetchMentionSuggestions,
  postComment,
  editComment,
  removeIdeaComment,
  removeIdea
} from "./thunk";

export interface BrainstormersState {
  brainstormer: Partial<Brainstormer>;
  brainstormers: Array<
    Partial<
      Brainstormer & {
        ideas: { meta: { count: number }; ideaList: Array<Partial<Idea>> };
      }
    >
  >;
  ideas: Partial<Array<Idea>>;
  meta: { count: number };
  idea: Partial<Idea>;
  comments: Partial<Array<IdeaComments>>;
  mentions: Partial<any>;
  drawerStatus: "none" | "contribution" | "update";
  status:
    | "idle"
    | "brainstormerFetching"
    | "ideasFetching"
    | "ideaFetching"
    | "commentsFetching"
    | "ideaSaving"
    | "ideaUpdated"
    | "closingIdea"
    | "voteSaving"
    | "ideaReporting"
    | "commentReporting"
    | "brainstormersFetching"
    | "rejected"
    | "fetchingMentions"
    | "commentSaving"
    | "commentSaved"
    | "editCommentSaved"
    | "commentError"
    | "commentDeleting"
    | "commentDeleted"
    | "ideaDeleting"
    | "ideaDeleted"
    | "editTriggered";
  brainstormersError: string | null;
  ideaError: string | null;
  votingError: string | null;
  mentionError: string | null;
  commentError: string | null;
}

const initialState: BrainstormersState = {
  brainstormer: {},
  ideas: [],
  meta: { count: null },
  idea: null,
  comments: null,
  mentions: [],
  status: "idle",
  drawerStatus: "none",
  brainstormersError: null,
  ideaError: null,
  votingError: null,
  brainstormers: [],
  mentionError: null,
  commentError: null
};

export const brainstormersSlice = createSlice({
  name: "brainstormers",
  initialState,
  reducers: {
    resetIdea: (state) => {
      state.idea = initialState.idea;
    },
    resetComments: (state) => {
      state.comments = initialState.comments;
    },
    resetVotingStatus: (state) => {
      state.votingError = null;
    },
    resetIdeaError: (state) => {
      state.ideaError = null;
    },
    updateIdeas: (state, action) => {
      const { id, idea } = action.payload;
      const index = state.ideas.findIndex((item) => item.id === id);
      if (index !== -1) {
        state.ideas[index] = idea;
      }
    },
    updateIdea: (state, action) => {
      state.idea = action.payload;
    },
    setCloseIdeaStatus: (state, action) => {
      state.status = action.payload;
    },
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setIdeaTransitionDrawer: (state, action) => {
      state.drawerStatus = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(hydrate, (state, action: PayloadAction<string>) => ({
        ...state,
        ...action.payload[brainstormersSlice.name]
      }))
      .addCase(fetchBrainstormer.pending, (state) => {
        state.status = "brainstormerFetching";
      })
      .addCase(fetchBrainstormer.fulfilled, (state, action) => {
        state.status = "idle";
        state.brainstormer = action.payload;
      })
      .addCase(fetchBrainstormer.rejected, (state, action) => {
        const { statusText } = action.payload as ResponsePayload;

        state.status = "idle";
        state.brainstormersError = statusText || "rejected";
      })
      .addCase(fetchBrainstormers.pending, (state) => {
        state.status = "brainstormersFetching";
      })
      .addCase(fetchBrainstormers.fulfilled, (state, action) => {
        state.status = "idle";
        state.brainstormers = action.payload;
      })
      .addCase(fetchBrainstormers.rejected, (state, action) => {
        const { statusText } = action.payload as ResponsePayload;

        state.status = "idle";
        state.brainstormersError = statusText || "rejected";
      })
      .addCase(fetchIdeas.pending, (state) => {
        state.status = "ideasFetching";
      })
      .addCase(fetchIdeas.fulfilled, (state, action) => {
        const { ideaList, meta, page } = action.payload;

        state.status = "idle";
        if (page === 1) {
          state.ideas = ideaList;
        } else {
          state.ideas = [...state.ideas, ...ideaList];
        }
        state.meta = meta;
      })
      .addCase(fetchIdeas.rejected, (state, action) => {
        const { statusText } = action.payload as ResponsePayload;

        state.status = "idle";
        state.brainstormersError = statusText || "rejected";
      })
      .addCase(saveIdea.pending, (state) => {
        state.status = "ideaSaving";
      })
      .addCase(saveIdea.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(saveIdea.rejected, (state, action) => {
        const { statusText } = action.payload as ResponsePayload;

        state.status = "idle";
        state.ideaError = statusText || "rejected";
      })

      .addCase(updateOneIdea.pending, (state) => {
        state.status = "ideaSaving";
      })
      .addCase(updateOneIdea.fulfilled, (state) => {
        state.status = "ideaUpdated";
      })
      .addCase(updateOneIdea.rejected, (state, action) => {
        const { statusText } = action.payload as ResponsePayload;
        state.status = "idle";
        state.ideaError = statusText;
      })

      .addCase(refreshOneIdea.pending, (state) => {
        state.status = "ideaFetching";
      })
      .addCase(refreshOneIdea.fulfilled, (state, action) => {
        const { id } = action.payload as Idea;
        const index = state.ideas.findIndex((item) => item.id === id);
        if (index !== -1) {
          state.ideas[index] = action.payload as Idea;
        }
        state.status = "idle";
      })
      .addCase(refreshOneIdea.rejected, (state, action) => {
        const { statusText } = action.payload as ResponsePayload;
        state.status = "idle";
        state.ideaError = statusText || "rejected";
      })

      .addCase(saveVote.pending, (state) => {
        state.status = "voteSaving";
      })
      .addCase(saveVote.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(saveVote.rejected, (state, action) => {
        const { statusText } = action.payload as ResponsePayload;

        state.status = "idle";
        state.votingError = statusText || "rejected";
      })
      .addCase(removeVote.pending, (state) => {
        state.status = "voteSaving";
      })
      .addCase(removeVote.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(removeVote.rejected, (state, action) => {
        const { statusText } = action.payload as ResponsePayload;

        state.status = "idle";
        state.votingError = statusText || "rejected";
      })
      .addCase(fetchIdea.pending, (state) => {
        state.status = "ideaFetching";
      })
      .addCase(fetchIdea.fulfilled, (state, action) => {
        state.status = "idle";
        state.idea = action.payload;
      })
      .addCase(fetchIdea.rejected, (state, action) => {
        const { statusText } = action.payload as ResponsePayload;
        state.status = "idle";
        state.ideaError = statusText || "rejected";
      })
      .addCase(fetchComments.pending, (state) => {
        state.status = "commentsFetching";
      })
      .addCase(fetchComments.fulfilled, (state, action) => {
        state.status = "idle";
        state.comments = action.payload;
      })
      .addCase(fetchComments.rejected, (state, action) => {
        const { statusText } = action.payload as ResponsePayload;
        state.status = "idle";
        state.ideaError = statusText || "rejected";
      })
      .addCase(reportIdea.pending, (state) => {
        state.status = "ideaReporting";
      })
      .addCase(reportIdea.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(reportIdea.rejected, (state, action) => {
        const { statusText } = action.payload as ResponsePayload;

        state.status = "idle";
        state.ideaError = statusText || "rejected";
      })
      .addCase(reportIdeaComment.pending, (state) => {
        state.status = "commentReporting";
      })
      .addCase(reportIdeaComment.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(reportIdeaComment.rejected, (state, action) => {
        const { statusText } = action.payload as ResponsePayload;

        state.status = "idle";
        state.ideaError = statusText || "rejected";
      })
      .addCase(fetchMentionSuggestions.pending, (state) => {
        state.status = "fetchingMentions";
      })
      .addCase(fetchMentionSuggestions.fulfilled, (state, action) => {
        state.status = "idle";
        state.mentions = action.payload;
      })
      .addCase(fetchMentionSuggestions.rejected, (state, action) => {
        const { statusText } = action.payload as ResponsePayload;
        state.status = "idle";
        state.mentionError = statusText || "rejected";
        state.mentions = [];
      })
      .addCase(postComment.pending, (state) => {
        state.status = "commentSaving";
        state.commentError = null;
      })
      .addCase(postComment.fulfilled, (state, action) => {
        state.status = "commentSaved";
        state.commentError = null;
      })
      .addCase(postComment.rejected, (state, action) => {
        const { statusText } = action.payload as ResponsePayload;
        state.status = "commentError";
        state.commentError = statusText || "rejected";
      })
      .addCase(editComment.rejected, (state, action) => {
        const { statusText } = action.payload as ResponsePayload;
        state.status = "commentError";
        state.commentError = statusText || "rejected";
      })
      .addCase(editComment.pending, (state) => {
        state.status = "commentSaving";
        state.commentError = null;
      })
      .addCase(editComment.fulfilled, (state, action) => {
        const { id } = action.payload as IdeaComments;
        const index = state.comments?.findIndex((item) => item.id === id);
        if (index !== -1) {
          state.comments[index] = {
            ...state.comments[index],
            ...(action.payload as IdeaComments)
          };
        }
        state.status = "editCommentSaved";
        state.commentError = null;
      })
      .addCase(removeIdeaComment.pending, (state) => {
        state.status = "commentDeleting";
      })
      .addCase(removeIdeaComment.fulfilled, (state) => {
        state.status = "commentDeleted";
        state.commentError = null;
      })
      .addCase(removeIdeaComment.rejected, (state, action) => {
        const { statusText } = action.payload as ResponsePayload;

        state.status = "idle";
        state.commentError = statusText || "rejected";
      })
      .addCase(removeIdea.pending, (state) => {
        state.status = "ideaDeleting";
      })
      .addCase(removeIdea.fulfilled, (state) => {
        state.status = "ideaDeleted";
      })
      .addCase(removeIdea.rejected, (state, action) => {
        const { statusText } = action.payload as ResponsePayload;

        state.status = "idle";
        state.ideaError = statusText || "rejected";
      });
  }
});

export const {
  resetIdea,
  resetComments,
  resetVotingStatus,
  resetIdeaError,
  updateIdeas,
  updateIdea,
  setCloseIdeaStatus,
  setStatus,
  setIdeaTransitionDrawer
} = brainstormersSlice.actions;
